import { useState, useEffect, useCallback, useMemo } from 'react'
import { ancestorUids } from '../lib/asset-type-tree'
import { matchesWithPath } from '../lib/assetPath'
import { useParams, useSearch, useNavigate } from '@tanstack/react-router'
import { Button } from './ui/button'
import { useAssets, useAssetTypes, useCreateAsset, useChildAssetTypes, useAsset } from '../hooks/useQueries'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { useApi } from '../api/hooks'
import { useTranslation } from 'react-i18next'
import { EquipmentTypePicker, type AssetTypeLite } from './equipment/EquipmentTypePicker'
import { PageHeader } from './shared/PageHeader'
import { AssetTypeTile, type AssetTypeLike } from './assets/AssetTypeTile'
import { SECTION_ICONS } from '../lib/section-icons'

interface AssetRow {
    id: string
    name: string
}

export function AssetCreatePageBasic() {
    const { t } = useTranslation('assets')
    const { org } = useParams({ from: '/$org/assets/createBasic' })
    const search = useSearch({ from: '/$org/assets/createBasic' })
    const navigate = useNavigate()

    const [parentAssetUid, setParentAssetUid] = useState<string>(search.parent_asset_uid || '')
    const [assetTypeUid, setAssetTypeUid] = useState<string>(search.asset_type_uid || '')
    const [assetRows, setAssetRows] = useState<AssetRow[]>([
        { id: crypto.randomUUID(), name: '' }
    ])
    const [quantity, setQuantity] = useState(1)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const { data: allAssets = [], isLoading: assetsLoading } = useAssets(org || 'demo')
    const { data: assetTypes = [], isLoading: assetTypesLoading } = useChildAssetTypes(org || 'demo', parentAssetUid)
    const { data: allAssetTypes = [], isLoading: allAssetTypesLoading } = useAssetTypes(org || 'demo') // For parent asset type lookup
    const { data: selectedParentAsset } = useAsset(org || 'demo', parentAssetUid || '') // Fetch specific parent asset if selected
    const createAssetMutation = useCreateAsset(org || 'demo')
    const { request } = useApi()

    // Helper functions for naming - memoized to prevent infinite loops
    const getAssetTypeName = useCallback((asset_type_uid: string) => {
        const assetTypeObj = assetTypes.find((type: any) => type.uid === asset_type_uid)
        if (!assetTypeObj) {
            return t('create.unknown_type')
        }
        if (typeof assetTypeObj.name === 'string') {
            return assetTypeObj.name
        }
        const nameObj = assetTypeObj.name as Record<string, string>
        return nameObj?.['fr'] || nameObj?.['en'] || assetTypeObj.code
    }, [assetTypes, t])

    const getParentAssetTypeName = useCallback((asset_type_uid: string) => {
        const assetTypeObj = allAssetTypes.find((type: any) => type.uid === asset_type_uid)
        if (!assetTypeObj) {
            return t('create.unknown_type')
        }
        if (typeof assetTypeObj.name === 'string') {
            return assetTypeObj.name
        }
        const nameObj = assetTypeObj.name as Record<string, string>
        return nameObj?.['fr'] || nameObj?.['en'] || assetTypeObj.code
    }, [allAssetTypes, t])

    // Once the type to create is chosen, the parent picker only offers the
    // types it may sit under (its `possible_parent_asset_type_uids`, and the
    // subtypes of those: a rule on `unit` covers `unit_housing`).
    const { data: typesWithParents = [] } = useAssetTypes(org || 'demo', true)
    const parentTypeCodes = useMemo(() => {
        if (!assetTypeUid) return ''
        const byUid = new Map(typesWithParents.map((type: any) => [type.uid, type]))
        const allowed: string[] = (byUid.get(assetTypeUid) as any)?.possible_parent_asset_type_uids ?? []
        if (allowed.length === 0) return ''
        return typesWithParents
            .filter((type: any) => ancestorUids(type.uid, byUid as Map<string, any>).some((uid) => allowed.includes(uid)))
            .map((type: any) => type.code)
            .join(',')
    }, [assetTypeUid, typesWithParents])

    // API search handlers for ApiSearchableSelect
    const searchAssets = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        try {
            const queryParams: Record<string, string> = {
                limit: '50',
                asset_chain: 'true'
            }

            if (parentTypeCodes) queryParams.asset_type_code = parentTypeCodes

            if (query) {
                queryParams.q = query
                queryParams.sort = 'name'
            } else {
                queryParams.sort = '-updated_at'
            }

            const response = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
                params: {
                    path: { orgCode: org },
                    query: queryParams
                }
            })

            // The API returns data directly in response.data, not response.data.data
            if ((response as any).data) {
                return matchesWithPath((response as any).data as any[]).map(({ row: asset, label }) => ({
                    value: asset.uid,
                    label: `${label} · ${getParentAssetTypeName(asset.asset_type_uid)}`
                }))
            }

            return []
        } catch (error) {
            console.error('Error searching assets:', error)
            return []
        }
    }, [org, request, getParentAssetTypeName, parentTypeCodes])

    const searchAssetTypes = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        // Source: when the caller has whitelisted specific UIDs, search the
        // unfiltered list (allAssetTypes) so equipment trades — which don't
        // list "building" as a possible parent — still show up. Otherwise,
        // search the parent-compatible subset.
        const searchSource = search.restrict_to_uids ? allAssetTypes : assetTypes
        const allowList = search.restrict_to_uids
            ? new Set(
                  search.restrict_to_uids
                      .split(',')
                      .map((u) => u.trim())
                      .filter(Boolean),
              )
            : null
        const labelFor = search.restrict_to_uids ? getParentAssetTypeName : getAssetTypeName

        let options: ApiSearchableSelectOption[] = searchSource
            .filter((type: any) => !allowList || allowList.has(type.uid))
            .map((type: any) => ({
                value: type.uid,
                label: labelFor(type.uid),
            }))

        if (query && query.length >= 1) {
            const lowerQuery = query.toLowerCase()
            options = options.filter((option: ApiSearchableSelectOption) =>
                option.label.toLowerCase().includes(lowerQuery)
            )
        }

        return options
    }, [assetTypes, allAssetTypes, getAssetTypeName, getParentAssetTypeName, search.restrict_to_uids])

    // Initial options for selects - memoized to prevent unnecessary re-renders
    const assetInitialOptions = useMemo((): ApiSearchableSelectOption[] => {
        const options = allAssets.slice(0, 20).map((asset: any) => ({
            value: asset.uid,
            label: `${asset.name} (${getParentAssetTypeName(asset.asset_type_uid)})`
        }))
        
        // If there's a selected parent asset, ensure it's in the options
        if (parentAssetUid && selectedParentAsset) {
            // First check if it's already in the options
            if (!options.some(option => option.value === parentAssetUid)) {
                options.unshift({
                    value: selectedParentAsset.uid,
                    label: `${selectedParentAsset.name} (${getParentAssetTypeName(selectedParentAsset.asset_type_uid)})`
                })
            }
        } else if (parentAssetUid && !selectedParentAsset) {
            // If we have a parentAssetUid but haven't loaded the asset yet, add placeholder
            if (!options.some(option => option.value === parentAssetUid)) {
                options.unshift({
                    value: parentAssetUid,
                    label: t('create.loading_selected_asset')
                })
            }
        }
        
        return options
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allAssets, getParentAssetTypeName, parentAssetUid, selectedParentAsset])

    // Optional UID allow-list passed by the caller (e.g. the Equipment tab
    // narrows the dropdown to the `corps d'état` asset types only). When
    // set, we trust the caller and bypass the implicit parent-compatibility
    // filter from useChildAssetTypes — equipment types intentionally don't
    // list "building" in their possible_parent_asset_type_ids, but we still
    // want to surface them when the caller has whitelisted them.
    const restrictUidSet = useMemo<Set<string> | null>(() => {
        if (!search.restrict_to_uids) return null
        const uids = search.restrict_to_uids
            .split(',')
            .map((u) => u.trim())
            .filter(Boolean)
        return uids.length > 0 ? new Set(uids) : null
    }, [search.restrict_to_uids])

    const assetTypeInitialOptions = useMemo((): ApiSearchableSelectOption[] => {
        if (restrictUidSet) {
            return allAssetTypes
                .filter((type: any) => restrictUidSet.has(type.uid))
                .map((type: any) => ({
                    value: type.uid,
                    label: getParentAssetTypeName(type.uid),
                }))
        }
        return assetTypes.map((type: any) => ({
            value: type.uid,
            label: getAssetTypeName(type.uid),
        }))
    }, [assetTypes, allAssetTypes, getAssetTypeName, getParentAssetTypeName, restrictUidSet])

    // Initialize form with query parameters
    useEffect(() => {
        if (search.parent_asset_uid) {
            setParentAssetUid(search.parent_asset_uid)
        }
        if (search.asset_type_uid) {
            setAssetTypeUid(search.asset_type_uid)
        }
    }, [search])

    const updateAssetName = (id: string, name: string) => {
        const updatedRows = assetRows.map(row =>
            row.id === id ? { ...row, name } : row
        )

        // If we just filled a row and there's no empty row, add one
        const hasEmptyRow = updatedRows.some(row => row.name.trim() === '')
        if (name.trim() !== '' && !hasEmptyRow) {
            updatedRows.push({ id: crypto.randomUUID(), name: '' })
        }

        // Remove empty rows except keep at least one
        const nonEmptyRows = updatedRows.filter(row => row.name.trim() !== '')
        const emptyRows = updatedRows.filter(row => row.name.trim() === '')

        // Always keep at least one empty row, but not more than one
        const finalRows = [...nonEmptyRows]
        if (emptyRows.length === 0 || (nonEmptyRows.length === 0 && emptyRows.length === 0)) {
            finalRows.push({ id: crypto.randomUUID(), name: '' })
        } else if (emptyRows.length > 0) {
            finalRows.push(emptyRows[0]) // Keep only the first empty row
        }

        setAssetRows(finalRows)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!assetTypeUid) {
            setError(t('create.error_no_type'))
            return
        }

        const validAssets = assetRows.filter(row => row.name.trim())
        if (validAssets.length === 0) {
            setError(t('create.error_no_name'))
            return
        }

        setIsSubmitting(true)
        setError(null)

        try {
            const promises = validAssets.map(row =>
                createAssetMutation.mutateAsync({
                    name: row.name.trim(),
                    asset_type_uid: assetTypeUid,
                    ...(parentAssetUid && { parent_asset_uid: parentAssetUid }),
                    ...(isMultipleType && quantity > 1 && { quantity: quantity as any })
                })
            )

            await Promise.all(promises)

            // Navigate to parent asset page if parent_asset_uid is provided, otherwise to assets page
            if (parentAssetUid) {
                navigate({ to: '/$org/assets/$assetUid', params: { org: org || 'demo', assetUid: parentAssetUid } })
            } else {
                navigate({ to: '/$org/assets', params: { org: org || 'demo' }, search: { showBuildingGroups: true, showBuildings: true, q: '', asset_type_uid: undefined } as any })
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : t('create.error_failed'))
        } finally {
            setIsSubmitting(false)
        }
    }

    const selectedAssetType = useMemo(() => {
        if (!assetTypeUid) return null
        return assetTypes.find((t: any) => t.uid === assetTypeUid) ||
               allAssetTypes.find((t: any) => t.uid === assetTypeUid) || null
    }, [assetTypeUid, assetTypes, allAssetTypes])
    const isMultipleType = Boolean((selectedAssetType as any)?.properties?.multiple)

    const isEquipmentType = (at: any) => {
        if (at.kind === 'equipment') return true
        if (at.kind) return false
        return typeof at.code === 'string' && /^trade_/.test(at.code)
    }

    // How the parent's allowed children split between equipment and everything
    // else. A parent whose children are ONLY equipment gets the hierarchical
    // picker in place of the field (spec 2026-05-05-01), ignoring
    // `restrict_to_uids` (kept on the route for backward compatibility with
    // deep-links). A parent that also allows structural children keeps the
    // plain select — replacing it would hide those children behind an
    // equipment-only browser, which is how adding a Building under a Building
    // Group broke once the first concrete equipment child of `building_group`
    // was seeded (spec 2026-08-16-01's distribution networks).
    const { useEquipmentPicker, offerEquipmentBrowser } = useMemo(() => {
        if (!parentAssetUid) return { useEquipmentPicker: false, offerEquipmentBrowser: false }
        const hasEquipment = assetTypes.some(isEquipmentType)
        const hasOther = assetTypes.some((at: any) => !isEquipmentType(at))
        return {
            useEquipmentPicker: hasEquipment && !hasOther,
            offerEquipmentBrowser: hasEquipment && hasOther,
        }
    }, [parentAssetUid, assetTypes])

    const [pickerOpen, setPickerOpen] = useState(false)

    const selectedPickerLabel = useMemo(() => {
        if (!selectedAssetType) return null
        const at = selectedAssetType as { name?: unknown; code: string }
        const lang = (typeof navigator !== 'undefined' && navigator.language?.slice(0, 2)) || 'en'
        return typeof at.name === 'string'
            ? at.name
            : ((at.name as Record<string, string> | undefined)?.[lang] ??
               (at.name as Record<string, string> | undefined)?.['en'] ??
               at.code)
    }, [selectedAssetType])

    const handlePickerPick = (assetType: AssetTypeLite) => {
        setAssetTypeUid(assetType.uid)
    }

    const isLoading = assetsLoading || assetTypesLoading || allAssetTypesLoading

    return (
        <div className="space-y-6">
            <PageHeader
                icon={SECTION_ICONS.assets}
                title={t('create.title')}
                actions={
                <Button
                    onClick={() => {
                        if (parentAssetUid) {
                            navigate({ to: '/$org/assets/$assetUid', params: { org: org || 'demo', assetUid: parentAssetUid } })
                        } else {
                            navigate({ to: '/$org/assets', params: { org: org || 'demo' }, search: { showBuildingGroups: true, showBuildings: true, q: '', asset_type_uid: undefined } as any })
                        }
                    }}
                    variant="outline"
                >
                    {t('create.cancel')}
                </Button>
                }
            />

            {isLoading ? (
                <div className="bg-background p-6 rounded-lg border">
                    <div className="text-center text-muted-foreground">{t('create.loading')}</div>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Asset Type Selection */}
                    <div className="bg-background p-6 rounded-lg border">
                        <h2 className="text-lg font-semibold text-foreground mb-4">{t('create.configuration_title')}</h2>

                        <div className="space-y-4">
                            {/* Parent Asset */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    {t('create.parent_asset_label')}
                                </label>
                                <ApiSearchableSelect
                                    key={parentTypeCodes || 'any-parent'}
                                    value={parentAssetUid}
                                    placeholder={t('create.parent_asset_placeholder')}
                                    searchPlaceholder={t('create.parent_asset_search_placeholder')}
                                    emptyMessage={t('create.parent_asset_empty')}
                                    onSearch={searchAssets}
                                    initialOptions={assetInitialOptions}
                                    onValueChange={setParentAssetUid}
                                    minSearchLength={2}
                                    disabled={isSubmitting}
                                />
                            </div>

                            {/* Asset Type */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    {useEquipmentPicker ? t('create_basic.asset_type_picker_label') : t('create.asset_type_label')} <span className="text-destructive">*</span>
                                </label>
                                {useEquipmentPicker ? (
                                    <EquipmentTypePicker
                                        org={org || 'demo'}
                                        open={pickerOpen}
                                        onOpenChange={setPickerOpen}
                                        onPick={handlePickerPick}
                                        ariaLabel={t('equipment_picker.search_aria_label')}
                                        showTradeChipFilters
                                    >
                                        <button
                                            type="button"
                                            disabled={isSubmitting}
                                            className="w-full flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring disabled:cursor-not-allowed disabled:opacity-50"
                                            data-testid="asset-type-picker-trigger"
                                        >
                                            <span className={`flex items-center gap-2 min-w-0 ${selectedPickerLabel ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                {selectedAssetType ? (
                                                    <AssetTypeTile type={selectedAssetType as AssetTypeLike} size="sm" />
                                                ) : null}
                                                <span className="truncate">
                                                    {selectedPickerLabel || t('create.asset_type_placeholder')}
                                                </span>
                                            </span>
                                            <span aria-hidden className="text-muted-foreground">▾</span>
                                        </button>
                                    </EquipmentTypePicker>
                                ) : (
                                    <>
                                        <ApiSearchableSelect
                                            value={assetTypeUid}
                                            placeholder={t('create.asset_type_placeholder')}
                                            searchPlaceholder={t('create.asset_type_search_placeholder')}
                                            emptyMessage={t('create.asset_type_empty')}
                                            onSearch={searchAssetTypes}
                                            initialOptions={assetTypeInitialOptions}
                                            onValueChange={setAssetTypeUid}
                                            minSearchLength={0}
                                            disabled={isSubmitting}
                                            data-testid="asset-type-select"
                                        />
                                        {offerEquipmentBrowser && (
                                            <EquipmentTypePicker
                                                org={org || 'demo'}
                                                open={pickerOpen}
                                                onOpenChange={setPickerOpen}
                                                onPick={handlePickerPick}
                                                ariaLabel={t('equipment_picker.search_aria_label')}
                                                showTradeChipFilters
                                            >
                                                <button
                                                    type="button"
                                                    disabled={isSubmitting}
                                                    className="mt-2 text-sm text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                                    data-testid="asset-type-browse-equipment-btn"
                                                >
                                                    {t('create_basic.browse_equipment_types')}
                                                </button>
                                            </EquipmentTypePicker>
                                        )}
                                    </>
                                )}
                            </div>
                            {/* Quantity (only for multiple types) */}
                            {isMultipleType && (
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        {t('create.quantity_label')}
                                    </label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={32767}
                                        value={quantity}
                                        onChange={(e) => setQuantity(Math.max(1, Math.min(32767, parseInt(e.target.value) || 1)))}
                                        className="block w-32 px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                        data-testid="asset-create-quantity-input"
                                        disabled={isSubmitting}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Assets to Create */}
                    <div className="bg-background p-6 rounded-lg border">
                        <div className="mb-4">
                            <h2 className="text-lg font-semibold text-foreground">{t('create.assets_to_create_title')}</h2>
                            <p className="text-sm text-muted-foreground mt-1">{t('create.assets_to_create_hint')}</p>
                        </div>

                        <div className="space-y-3">
                            {assetRows.map((row) => (
                                <div key={row.id} className="flex gap-3 items-center">
                                    <div className="flex-1">
                                        <input
                                            type="text"
                                            value={row.name}
                                            onChange={(e) => updateAssetName(row.id, e.target.value)}
                                            placeholder={t('create.asset_name_placeholder')}
                                            className="block w-full px-3 py-2 border border-input bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 text-sm text-muted-foreground">
                            {t('create.ready', { count: assetRows.filter(row => row.name.trim()).length })}
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
                            <div className="text-destructive text-sm">{error}</div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            onClick={() => {
                                if (parentAssetUid) {
                                    navigate({ to: '/$org/assets/$assetUid', params: { org: org || 'demo', assetUid: parentAssetUid } })
                                } else {
                                    navigate({ to: '/$org/assets', params: { org: org || 'demo' }, search: { showBuildingGroups: true, showBuildings: true, q: '', asset_type_uid: undefined } as any })
                                }
                            }}
                            variant="outline"
                            disabled={isSubmitting}
                        >
                            {t('create.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting || !assetTypeUid || assetRows.filter(row => row.name.trim()).length === 0}
                        >
                            {isSubmitting ? t('create.submitting') : t('create.submit', { count: assetRows.filter(row => row.name.trim()).length })}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    )
} 